import { AppConfiguration } from "config";
import React, { useEffect, useMemo, useState } from "react";
import { MetaTags } from "react-meta-tags";
import { Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Row, UncontrolledDropdown } from "reactstrap";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import { getWhitelistedBrandsAdminUsers, getWhitelistedBrandsUserMappings, updateWhitelistedBrandsAdminUsers } from "store/admin/whitelisted-brands/actions";
import { useDispatch, useSelector } from "react-redux";
import { updateAdminInternalUser } from "store/admin/admin-internal-user/actions";
import WhitelabeledBrandsBreadcrumb from "./WhitelabeledBrandsBreadcrumb";
import { ADMIN_STATUS, REQUIRED_FEATURE_FLAGS, TABLE_COLMN_NAMES, USER_TYPES, WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS } from "helpers/sf_constants";
import { getPrimaryTextClass, forColumnHeaderWrapper } from "helpers/sf_utils";
import ModifyLoadingIcon from "components/Common/Loadings/ModifyLoadingIcon";
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton";
import useFeatureFlags from "hooks/useFeatureFlags";
import UserStyle from '../Users/Users.module.css';
import SSOModal from "./Modal/SSOModal";
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert";
import WhitelistedBrandUsersFilterModal from "./WhitelistedBrandUsersFilterModal";
import { customSortCaret, rowStyle } from "helpers/bootstrap_table_helper";

const WhitelistedBrandsUsers = () => {

   const {
      all_admin_employees,
      all_admin_employees_loading,
      whitelisted_brands,
      whitelisted_brands_loading,
      whitelisted_brands_error,
      whitelisted_brands_user_mappings,
      whitelisted_brands_user_mappings_loading,
      whitelisted_brands_user_mappings_error,
      whitelisted_brands_admin_users,
      whitelisted_brands_admin_users_loading,
      whitelisted_brands_admin_users_error,
      admin_manage_employees,
      current_user
   } = useSelector((state) => {
      return {
         admin_manage_employees: state.AdminManageInternalUsers.admin_manage_employees,
         all_admin_employees: state.AdminManageInternalUsers.all_admin_employees,
         whitelisted_brands: state.WhitelistedBrandLogin.whitelisted_brands,
         whitelisted_brands_loading: state.WhitelistedBrandLogin.whitelisted_brands_loading,
         whitelisted_brands_error: state.WhitelistedBrandLogin.whitelisted_brands_error,
         whitelisted_brands_user_mappings: state.WhitelistedBrandLogin.whitelisted_brands_user_mappings,
         whitelisted_brands_user_mappings_loading: state.WhitelistedBrandLogin.whitelisted_brands_user_mappings_loading,
         whitelisted_brands_user_mappings_error: state.WhitelistedBrandLogin.whitelisted_brands_user_mappings_error,
         whitelisted_brands_admin_users: state.WhitelistedBrandLogin.whitelisted_brands_admin_users,
         whitelisted_brands_admin_users_loading: state.WhitelistedBrandLogin.whitelisted_brands_admin_users_loading,
         whitelisted_brands_admin_users_error: state.WhitelistedBrandLogin.whitelisted_brands_admin_users_error,
         current_user: state.LoginAdminUser.current_user,
         all_admin_employees_loading: state.AdminManageInternalUsers.loading,
      }
   });

   const dispatch = useDispatch();

   useEffect(() => {
      whitelisted_brands.length && dispatch(getWhitelistedBrandsAdminUsers(whitelisted_brands));
      dispatch(getWhitelistedBrandsUserMappings())
   }, [whitelisted_brands])

   const [isFeatureFlagActive] = useFeatureFlags();

   const [editIconRow, setEditIconRow] = useState(null);
   const [loadingCell, setLoadingCell] = useState({ row: null, col: null });
   const [SSOModalState, setSSOModalState] = useState({ active: false });
   const [isActiveFilterModal, setIsActiveFilterModal] = useState(false);

   const [filterModal, setFilterModal] = useState({
      name: false,
      name_value: "",

      id: false,
      id_value: "",
      id_state_value: "",

      email: false,
      email_value: "",

      brand: false,
      brand_value: null,

      status: false,
      status_value: null
   });

   const [filteredColumns, setFilteredColumns] = useState([]);
   const [activeFilteredColumnsState, setActiveFilteredColumnsState] = useState(null)

   const applyFilter = (filterModal = null, admin_users = null) => {
      if (!filterModal || !admin_users) {
         return admin_users; // Return the original array if no filter or data is provided
      }

      const statusMapping = {
         "paused": "inactive",
      };

      return admin_users.filter((user) => {
         let isMatch = true;

         if (filterModal.name) {
            let user_name = user.name.toLowerCase()
            isMatch = isMatch && user_name.includes(filterModal.name_value.toLowerCase());
         }

         if (filterModal.id && filterModal.id_state_value) {
            switch (filterModal.id_state_value) {
               case "Greater Than":
                  isMatch = isMatch && user.id > parseInt(filterModal.id_value);
                  break;
               case "Less Than":
                  isMatch = isMatch && user.id < parseInt(filterModal.id_value);
                  break;
               case "Equal To":
                  isMatch = isMatch && user.id === parseInt(filterModal.id_value);
                  break;
               default:
                  break;
            }
         }

         if (filterModal.email) {
            isMatch = isMatch && user.email.includes(filterModal.email_value.toLowerCase());
         }

         if (filterModal.brand && filterModal.brand_value) {
            const selectedBrands = JSON.parse(filterModal.brand_value);
            const userBrand = user.brand_name.toLowerCase();
            isMatch = isMatch && selectedBrands.some((selectedBrand) => selectedBrand.value.toLowerCase() === userBrand);
         }

         if (filterModal.status && filterModal.status_value) {
            const selectedStatuses = JSON.parse(filterModal.status_value);
            const userStatus = user.status.toLowerCase();
            // Map "paused" to "inactive" before checking for a match
            const mappedStatuses = selectedStatuses.map((selectedStatus) => statusMapping[selectedStatus.value.toLowerCase()] || selectedStatus.value.toLowerCase());
            isMatch = isMatch && mappedStatuses.includes(userStatus);
         }

         return isMatch;
      });
   };


   const allAdminUsers = useMemo(() => {
      const x_filtered_all_admin_employees = all_admin_employees?.map(admin => {
         return {
            id: admin?.id,
            name: admin?.first_name ? (admin?.first_name + " " + admin?.last_name) : "--",
            admin_role: admin?.admin_role,
            email: admin?.email,
            status: admin?.status,
            discarded_at: admin?.discarded_at,
            brand_id: null,
            brand_name: AppConfiguration.WEBSITE,
         }
      })

      const x_whitelisted_brands_admin_users = whitelisted_brands_admin_users?.map(brand => {
         const brand_id = brand?.id
         const brand_name = brand?.name
         const users = brand?.users?.map(user => {
            return {
               id: user?.id,
               name: user?.first_name ? user?.first_name + " " + user?.last_name : "--",
               admin_role: user?.admin_role,
               email: user?.email,
               status: user?.status,
               discarded_at: user?.discarded_at,
               brand_id: brand_id,
               brand_name: brand_name,
            }
         }
         )
         return users
      })
      const x_whitelisted_brands_admin_users_flattened = x_whitelisted_brands_admin_users?.flat()
      const combined_whitelisted_brands_admin_users = [...x_filtered_all_admin_employees, ...x_whitelisted_brands_admin_users_flattened]
      const filtered_whitelisted_brands_admin_users = applyFilter(filterModal, combined_whitelisted_brands_admin_users)
      return filtered_whitelisted_brands_admin_users
   }, [all_admin_employees, whitelisted_brands_admin_users, admin_manage_employees, filterModal])

   const onSubmitOpenFilterModal = (values) => {
      setFilterModal({ ...filterModal, ...values })
      setIsActiveFilterModal(false)
   }

   //For Filter Modal Open 
   const onClickOpenFilterModal = () => {
      setIsActiveFilterModal(true)
   }
   //For Filter Modal Close 
   const onCloseFilterModal = () => {
      setIsActiveFilterModal(false);
   }

   const onClickRemoveFilter = (fieldName) => {
      setFilterModal({ ...filterModal, [fieldName]: !fieldName, [fieldName + '_value']: "" })
      setFilteredColumns([])
   }

   const filterCount = useMemo(() => {
      return filteredColumns?.length
   }, [filteredColumns])

   const filterCheckedCount = useMemo(() => {
      let count = 0
      for (const k in filterModal) {
         if (filterModal[k] === true && k !== 'isActiveModal') {
            count += 1
         }
      }
      return count
   }, [filterModal])

   useEffect(() => {
      let isUpdateStates = false;
      let isFilteredColumns = [];
      const activeFilteredColumns = {}

      if (filterModal?.name && filterModal?.name_value) {
         isUpdateStates = true;
         isFilteredColumns.push({ ...WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS.NAME, value: filterModal?.name_value })
         activeFilteredColumns.name = filterModal?.name;
         activeFilteredColumns.name_value = filterModal?.name_value;
      }

      if (filterModal?.email && filterModal?.email_value) {
         isUpdateStates = true;
         isFilteredColumns.push({ ...WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS.EMAIL, value: filterModal?.email_value })
         activeFilteredColumns.email = filterModal?.email;
         activeFilteredColumns.email_value = filterModal?.email_value;
      }

      if (filterModal?.id && filterModal?.id_value) {
         isUpdateStates = true;
         isFilteredColumns.push({ ...WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS.ID, value: filterModal?.id_state_value + " " + filterModal?.id_value })
         activeFilteredColumns.id = filterModal?.id;
         activeFilteredColumns.id_value = filterModal?.id_value;
         activeFilteredColumns.id_state_value = filterModal?.id_state_value;
      }

      if (filterModal?.brand && JSON.parse(filterModal?.brand_value)) {
         isUpdateStates = true;
         const state_array = JSON.parse(filterModal?.brand_value)
         state_array.length !== 0 && isFilteredColumns.push({ ...WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS.BRAND, value: state_array.map(el => el.value).join(', ') })
         activeFilteredColumns.brand = filterModal?.brand;
         activeFilteredColumns.brand_value = filterModal?.brand_value;
      }

      if (filterModal?.status && JSON.parse(filterModal?.status_value)) {
         isUpdateStates = true;
         const status_array = JSON.parse(filterModal?.status_value)
         status_array.length !== 0 && isFilteredColumns.push({ ...WHITELABELED_BRANDS_FILTERED_COLUMNS_OBJECTS.STATUS, value: status_array.map(el => el.value).join(', ') })
         activeFilteredColumns.status = filterModal?.status;
         activeFilteredColumns.status_value = filterModal?.status_value;
      }

      setActiveFilteredColumnsState(activeFilteredColumns);
      if (isUpdateStates) {
         applyFilter(activeFilteredColumns)
         setFilteredColumns(isFilteredColumns);
         return
      }
      applyFilter()
      setFilteredColumns([]);
   }, [filterModal])

   const brandOptions = useMemo(() => {
      const options = [];
      options.push({ label: 'insurance.io', value: 'insurance.io' });
      if (whitelisted_brands && whitelisted_brands.length > 0) {
         whitelisted_brands.forEach(brand => {
            options.push({ label: brand.name, value: brand.name });
         });
      }
      return options;
   }, [whitelisted_brands]);


   const { SearchBar } = Search

   const getColorForStatus = (status) => {
      switch (status) {
         case ADMIN_STATUS.ACTIVE:
            return 'sfgreen'
         case ADMIN_STATUS.INACTIVE:
            return 'sfred'
         case ADMIN_STATUS.PENDING:
            return 'sfred'
         default:
            return 'sfred'
      }
   }

   const isCurrentUserSuperAdmin = () => {
      return current_user?.admin_role?.name.toLowerCase() === USER_TYPES.SUPER_ADMIN.toLowerCase()
   }

   const onClickOpenSSOModal = (userMappings, whitelisted_brands_admin_users, whitelisted_brands, admin_user, brand_id) => {
      if (!isCurrentUserSuperAdmin() || admin_user?.discarded_at || brand_id) return;
      setSSOModalState({ active: true, userMappings, whitelisted_brands_admin_users, whitelisted_brands, admin_user });
   }

   const getUserMappingCount = (user) => {
      return whitelisted_brands_user_mappings?.filter(mapping => mapping?.admin_user_id === user?.id && mapping?.whitelabeled_brand_admin_user_id !== null)?.length
   }

   const rowEvents = {
      onMouseEnter: (_e, _column, rowIndex) => {
         setEditIconRow(rowIndex);
      },
      onMouseLeave: (_e, _column, _rowIndex) => {
         setEditIconRow(null);
      },
   };

   const onUpdateStatus = (row, status) => {
      if (row?.id === current_user?.id && !row?.brand_id) {
         ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: "You cannot change your own status." });
         return;
      }

      if (row?.admin_role?.name.toLowerCase() === USER_TYPES.SUPER_ADMIN.toLowerCase() && current_user?.admin_role?.name.toLowerCase() !== USER_TYPES.SUPER_ADMIN.toLowerCase()) {
         ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: "You cannot change the status of Super Admin" })
         return;
      }

      const whitelisted_brand = whitelisted_brands?.find(brand => brand?.id === row?.brand_id)

      const data = {
         admin_user: {
            status: status,
            id: row.id,
            brand_id: row?.brand_id,
            brand_url: whitelisted_brand?.api_url
         }
      }

      //if brand is not there call our brand api
      if (row?.brand_id) {
         dispatch(updateWhitelistedBrandsAdminUsers(data));
      }
      else {
         dispatch(updateAdminInternalUser(data));
      }
   }

   const piiColumns = [
      {
         dataField: "status",
         text: "Status",
         sort: true,
         events: {
            onClick: (e, column, columnIndex, row, rowIndex) => {
               {
                  setLoadingCell({ row: rowIndex, col: TABLE_COLMN_NAMES.ACTION });
               }
            },
         },
         formatter: (cellContent, row, rowIndex) => (
            <div className="d-flex gap-3 justify-content-start">
               <UncontrolledDropdown>
                  <DropdownToggle
                     className="card-drop"
                     tag="i"
                  >
                     {
                        (whitelisted_brands_admin_users_loading === true || all_admin_employees_loading === true)
                           && (loadingCell.row === rowIndex)
                           && (loadingCell.col === TABLE_COLMN_NAMES.ACTION) ? (<ModifyLoadingIcon color="secondary" />) : (
                           <>
                              <i className={`mdi mdi-circle font-size-16 ${getColorForStatus(cellContent)}`} />
                              {
                                 <span className={`font-size-16 ${(rowIndex === editIconRow) ? '' : 'edit-hover-class'}`}>
                                    <a><i className="mdi mdi-chevron-down" /></a>
                                 </span>
                              }
                           </>
                        )
                     }
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-start">
                     <DropdownItem onClick={() => onUpdateStatus(row, ADMIN_STATUS.ACTIVE)}>
                        <div className="d-inline-flex align-content-center">
                           <i className='mdi mdi-circle font-size-16 sfgreen' />
                           <div className="ms-2 d-flex align-self-center">Active</div>
                        </div>
                     </DropdownItem>
                     <DropdownItem onClick={() => onUpdateStatus(row, ADMIN_STATUS.INACTIVE)}>
                        <div className="d-inline-flex align-content-center">
                           <i className='mdi mdi-circle font-size-16 sfred' />
                           <div className="ms-2 d-flex align-self-center">Paused</div>
                        </div>
                     </DropdownItem>
                  </DropdownMenu>
               </UncontrolledDropdown>
            </div>
         ),
      },
      {
         dataField: "id",
         text: "ID",
         sort: true,
         headerFormatter: forColumnHeaderWrapper,
      },
      {
         dataField: "name",
         text: "Name",
         headerFormatter: forColumnHeaderWrapper,
         sort: true,
      },
      {
         dataField: "email",
         text: "Email",
         headerFormatter: forColumnHeaderWrapper,
         sort: true,
      },
      {
         dataField: "brand_name",
         text: "Brand",
         headerFormatter: forColumnHeaderWrapper,
         sort: true,
      },
      {
         dataField: "brand_id",
         text: "SSO",
         hasInfoTooltip: true,
         tooltipDescription: 'Single Sign On',
         headerFormatter: forColumnHeaderWrapper,
         formatExtraData: whitelisted_brands_user_mappings,
         events: {
            onClick: (_e, _column, _columnIndex, _row, rowIndex) => {
            },
         },
         formatter: (brand_id, row, rowIndex, whitelisted_brands_user_mappings) => {
            const userMappings = whitelisted_brands_user_mappings?.filter(mapping => mapping?.admin_user_id === row?.id)
            return (
               <div className="d-flex align-items-center">
                  <a onClick={() => onClickOpenSSOModal(userMappings, whitelisted_brands_admin_users, whitelisted_brands, row, brand_id)} className={brand_id || !isCurrentUserSuperAdmin() || row?.discarded_at ? 'text-black' : 'text-sfblue'}>
                     {getUserMappingCount(row, whitelisted_brands_user_mappings) > 0 && !brand_id ? `(${getUserMappingCount(row)}) Brand Activated ` : (brand_id || !isCurrentUserSuperAdmin() || row?.discarded_at ? "--" : "+ Add Brand")}
                  </a>
                  {
                     !(brand_id || !isCurrentUserSuperAdmin() || row?.discarded_at) &&
                     <span className={`ms-3 font-size-16 ${(rowIndex === editIconRow) ? '' : 'edit-hover-class'}`}>
                        <a onClick={() => onClickOpenSSOModal(userMappings, whitelisted_brands_admin_users, whitelisted_brands, row, brand_id)}><i className="mdi mdi-pencil text-success" /></a>
                     </span>
                  }
               </div>
            )
         },
      }
   ]

   const dynamicColumns = useMemo(() => {
      if (!whitelisted_brands_admin_users?.length) {
         return [];
      }

      return whitelisted_brands.map((brand, index) => {
         const dataField = `brands[${index}].whitelabeled_brand_admin_user_id`;
         return {
            dataField,
            text: brand.name,
            sort: true,
         };
      });
   }, [whitelisted_brands_admin_users])

   return (
      <React.Fragment>
         <SSOModal
            modalState={SSOModalState}
            onCloseClick={() => setSSOModalState({ active: false })}
         />
         <div className="page-content">
            <MetaTags>
               <title>Whitelabeled Brands Users | {AppConfiguration.WEBSITE}</title>
            </MetaTags>
            <Container fluid>
               {
                  isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_WHITELISTED_BRAND_USER_MAPPING) &&
                  <WhitelabeledBrandsBreadcrumb
                     breadcrumbItem="Users"
                  />
               }
               <WhitelistedBrandUsersFilterModal
                  isActive={isActiveFilterModal}
                  modalState={filterModal}
                  onSubmitModalState={onSubmitOpenFilterModal}
                  onClickModalClose={onCloseFilterModal}
                  brandOptions={brandOptions}
               />
               <Row>
                  <Col className="col-12">
                     <Card>
                        <CardBody>
                           <ToolkitProvider
                              keyField="uuid"
                              columns={piiColumns}
                              data={allAdminUsers}
                              search
                           >
                              {(toolkitProps) => (
                                 <React.Fragment>
                                    <Row>
                                       <Col md="auto" className="align-self-center">
                                          <FormGroup className="mb-0">
                                             <button
                                                className={`d-flex align-items-center btn btn-outline-primary ${(filterModal?.isActiveModal || filterCheckedCount !== 0) ? 'filter-btn' : ''} ${filterCount === 0 ? 'filter-btn-w' : ''}`}
                                                type='button'
                                                onClick={onClickOpenFilterModal}
                                             >
                                                <i className="bx bx-filter font-size-18 align-middle me-2" />
                                                Filters {filterCount != 0 && <span className={filterModal?.isActiveModal ? 'numberCircle' : 'numberCircle border-0'}> {filterCount} </span>}
                                             </button>
                                          </FormGroup>
                                       </Col>
                                       <Col md="auto" className="align-self-center">
                                          <div className="mb-0 mt-3">
                                             <div className="search-box d-inline-block mb-3">
                                                <div className="position-relative">
                                                   <SearchBar
                                                      className="rounded"
                                                      {...toolkitProps.searchProps}
                                                   />
                                                   <i className="bx bx-search-alt search-icon" />
                                                </div>
                                             </div>
                                          </div>
                                       </Col>
                                       <Col md="auto" className="align-self-center">
                                          <div className="d-flex flex-wrap align-items-center">
                                             {filteredColumns && filteredColumns.map((single_object, idx) => (
                                                <div key={idx} className={`mx-2 mb-2 ${UserStyle.selected_filter}`}>
                                                   <div className="d-flex align-items-center">
                                                      <span>{single_object?.name} : <b>{single_object?.value}</b></span>
                                                      <a className={`ms-2 ${getPrimaryTextClass()} font-size-18`} onClick={() => onClickRemoveFilter(single_object?.fieldName)} style={{ cursor: "pointer" }}>
                                                         <span aria-hidden="true">&times;</span>
                                                      </a>
                                                   </div>
                                                </div>
                                             ))}
                                          </div>
                                       </Col>
                                    </Row>
                                    <Row className="">
                                       <Col md={12} xl="12">
                                          <div className="sfTable-Container">
                                             {
                                                !dynamicColumns.length > 0 ?
                                                   <SfLoadingSkeleton rows={8} cols={6} /> :
                                                   <BootstrapTable
                                                      keyField="id"
                                                      responsive
                                                      bordered={false}
                                                      striped={false}
                                                      data={allAdminUsers || []}
                                                      rowEvents={rowEvents}
                                                      rowClasses={(row, _) => (row.discarded_at ? 'tr-hover-deleted' : 'tr-hover')}
                                                      rowStyle={(row) => rowStyle(row, row.discarded_at)}
                                                      filter={filterFactory()}
                                                      classes="table align-middle table-nowrap text-black"
                                                      headerClasses="sftableHeader text-black thead-h50"
                                                      headerWrapperClasses="thead-light align-text-top"
                                                      noDataIndication="You currently don't have any user mappings."
                                                      sort={{ sortCaret: customSortCaret }}
                                                      {...toolkitProps.baseProps}
                                                   />
                                             }
                                          </div>
                                       </Col>
                                    </Row>
                                 </React.Fragment>
                              )}
                           </ToolkitProvider>
                        </CardBody>
                     </Card>
                  </Col>
               </Row>
            </Container>
         </div>
      </React.Fragment>
   )
}

export default WhitelistedBrandsUsers;